import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  CalendarDays, Check, Clock3, Loader2, MessageSquare,
  Send, ShieldCheck, TriangleAlert
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_API_BASE_URL}/api`;

const statusSteps = [
  { key: 'open', label: 'Request received' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'resolved', label: 'Completed' },
];

const statusRank = {
  open: 0,
  in_progress: 1,
  resolved: 2,
  closed: 2,
};

const formatDate = value => {
  if (!value) return 'Not scheduled';
  return new Date(value).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatDateTime = value => {
  if (!value) return '';
  return new Date(value).toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const TicketTracking = () => {
  const { token } = useParams();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comment, setComment] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentError, setCommentError] = useState('');

  useEffect(() => {
    axios.get(`${API}/ticket-tracking/${token}`)
      .then(response => setTask(response.data))
      .catch(err => setError(err.response?.data?.detail || 'This tracking link is unavailable.'))
      .finally(() => setLoading(false));
  }, [token]);

  const submitComment = async event => {
    event.preventDefault();
    const text = comment.trim();
    if (!text) return;
    setCommentSaving(true);
    setCommentError('');
    try {
      const response = await axios.post(`${API}/ticket-tracking/${token}/comments`, { text });
      setTask(current => ({
        ...current,
        comments: [...(current.comments || []), response.data],
        updated_at: response.data.created_at,
      }));
      setComment('');
    } catch (err) {
      setCommentError(err.response?.data?.detail || 'Your comment could not be added.');
    } finally {
      setCommentSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
          <TriangleAlert className="w-10 h-10 text-amber-500 mx-auto" />
          <h1 className="text-xl font-bold text-slate-900 mt-4">Tracking link unavailable</h1>
          <p className="text-sm text-slate-500 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  const activeRank = statusRank[task.status] ?? 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-950 text-white">
        <div className="max-w-4xl mx-auto px-6 py-7 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">WebMarketers</p>
            <h1 className="text-xl font-bold mt-1">Support Hub</h1>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            Private task tracking
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-7 sm:p-9 border-b border-slate-100">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge className={task.completed ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-blue-100 text-blue-700 hover:bg-blue-100'}>
                {task.completed ? 'Completed' : task.status.replace('_', ' ')}
              </Badge>
              <Badge variant="outline" className="capitalize">{task.priority} priority</Badge>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-950">{task.title}</h2>
            <p className="text-slate-600 leading-7 mt-4 whitespace-pre-wrap">{task.description}</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-px bg-slate-200 border-b border-slate-200">
            <div className="bg-white p-5">
              <p className="text-xs uppercase tracking-wider text-slate-400">Due date</p>
              <p className="font-semibold text-slate-800 mt-2 flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-slate-400" />{formatDate(task.due_date)}
              </p>
            </div>
            <div className="bg-white p-5">
              <p className="text-xs uppercase tracking-wider text-slate-400">Company</p>
              <p className="font-semibold text-slate-800 mt-2">{task.company_name || 'General support'}</p>
            </div>
            <div className="bg-white p-5">
              <p className="text-xs uppercase tracking-wider text-slate-400">Last updated</p>
              <p className="font-semibold text-slate-800 mt-2 flex items-center gap-2">
                <Clock3 className="w-4 h-4 text-slate-400" />{formatDate(task.updated_at)}
              </p>
            </div>
          </div>

          <div className="p-7 sm:p-9 border-b border-slate-100">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Task progress</p>
              <h3 className="text-xl font-bold text-slate-950 mt-1">
                {statusSteps[activeRank].label}
              </h3>
            </div>
            <div className="relative mt-7 grid sm:grid-cols-3 gap-3 sm:gap-0">
              <div className="hidden sm:block absolute top-5 left-[16.6667%] right-[16.6667%] h-0.5 bg-slate-200">
                <div
                  className="h-full bg-blue-600 transition-all"
                  style={{ width: `${Math.min(activeRank / 2, 1) * 100}%` }}
                />
              </div>
              {statusSteps.map((step, index) => {
                const isPast = index < activeRank;
                const isCurrent = index === activeRank;
                return (
                  <div key={step.key} className="relative flex sm:block items-center gap-4">
                    <div className={`relative z-10 sm:mx-auto w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2 ${
                      isCurrent
                        ? 'bg-blue-600 border-blue-600 text-white ring-4 ring-blue-100'
                        : isPast
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-white border-slate-300 text-slate-400'
                    }`}>
                      {isPast ? <Check className="w-5 h-5" /> : <span className="font-bold">{index + 1}</span>}
                    </div>
                    <div className="sm:text-center sm:mt-4 sm:px-2">
                      <p className={`font-bold ${isCurrent || isPast ? 'text-slate-900' : 'text-slate-400'}`}>{step.label}</p>
                      <p className={`text-xs mt-1 font-medium ${isCurrent ? 'text-blue-600' : 'text-slate-400'}`}>
                        {isCurrent ? 'Current status' : isPast ? 'Done' : 'Next'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="p-7 sm:p-9">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-950">Comments</h3>
                <p className="text-sm text-slate-500">Updates shared between you and the support team.</p>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              {(task.comments || []).length === 0 ? (
                <div className="border border-dashed border-slate-200 rounded-xl p-6 text-center text-sm text-slate-400">
                  No comments have been added yet.
                </div>
              ) : task.comments.map(item => (
                <div key={item.id} className={`flex ${item.role === 'requester' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[88%] rounded-2xl px-4 py-3 ${
                    item.role === 'requester'
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-slate-100 text-slate-800 rounded-bl-md'
                  }`}>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <p className={`text-xs font-bold ${item.role === 'requester' ? 'text-blue-100' : 'text-slate-600'}`}>{item.author}</p>
                      <p className={`text-[11px] ${item.role === 'requester' ? 'text-blue-200' : 'text-slate-400'}`}>{formatDateTime(item.created_at)}</p>
                    </div>
                    <p className="text-sm leading-6 whitespace-pre-wrap mt-1">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={submitComment} className="mt-7">
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="text-sm font-medium text-slate-700">
                  Commenting as <span className="font-bold">{task.requested_by_name || 'Requester'}</span>
                </p>
                <Badge variant="outline" className="text-xs text-slate-500">Requester</Badge>
              </div>
              {commentError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-3">{commentError}</p>
              )}
              <Textarea
                value={comment}
                onChange={event => setComment(event.target.value)}
                placeholder="Write a comment for the support team..."
                rows={4}
                maxLength={4000}
                className="resize-none"
              />
              <div className="flex items-center justify-between gap-4 mt-3">
                <p className="text-xs text-slate-400">{comment.length}/4000</p>
                <Button type="submit" disabled={commentSaving || !comment.trim()} className="gap-2 bg-blue-600 hover:bg-blue-700">
                  {commentSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Add comment
                </Button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TicketTracking;
